/**
 * Enhanced Product Helpers
 * Utility functions for locale detection, translations, and tab content rendering.
 * All functions are exposed as window globals so the inline Liquid script and
 * other external scripts can call them after this defer script loads.
 *
 * Relies on window globals set by the enhanced-product-information inline script:
 *   window.productTranslations, window.currentVariantMetafields,
 *   window.originalProductText, window.enhancedIsProductPage
 */

// ─── Locale ───────────────────────────────────────────────────────────────────

/**
 * Returns the current locale derived from the URL path.
 * Format: language-COUNTRY (e.g. es-US, fr-CA, en-CA).
 * Falls back to 'en-US'.
 */
function getCurrentLocale() {
  const pathname = window.location.pathname;
  const match = pathname.match(/^\/([a-z]{2}-[a-z]{2})(?:\/|$|\?)/);
  if (match) {
    const [language, country] = match[1].split('-');
    return `${language}-${country.toUpperCase()}`;
  }
  return 'en-US';
}

// ─── DMO translations ─────────────────────────────────────────────────────────

/**
 * Parses a translations JSON string from DMO offer data.
 * Returns the parsed object or null on failure.
 */
function parseDMOTranslations(translationsString) {
  if (!translationsString || translationsString.trim() === '') return null;
  try {
    return JSON.parse(translationsString);
  } catch {
    return null;
  }
}

/**
 * Gets a translated field from a DMO translations object,
 * trying multiple locale format variations before returning the fallback.
 */
function getDMOTranslatedValue(translations, field, fallback) {
  if (!translations) return fallback;
  const locale = getCurrentLocale();
  const variations = [
    locale,
    locale.toUpperCase(),
    locale.toLowerCase(),
    `${locale.split('-')[0].toLowerCase()}-${locale.split('-')[1].toUpperCase()}`
  ];
  for (const l of variations) {
    if (translations[l]) {
      const variantVal = translations[l].variant?.[field];
      if (variantVal && typeof variantVal === 'string' && variantVal.trim()) return variantVal;
      const productVal = translations[l].product?.[field];
      if (productVal && typeof productVal === 'string' && productVal.trim()) return productVal;
    }
  }
  return fallback;
}

/**
 * Applies DMO offer translations to the product title and description elements.
 * Only runs on non-product pages where DMO is the data source.
 */
function applyDMOTranslations() {
  // Only run on non-product pages (flag set by inline Liquid script)
  if (window.enhancedIsProductPage) return;
  if (!window.data?.theme?.productOffer?.offers) return;

  const flagshipOffer = window.data.theme.productOffer.offers.find(o => o.type === 'FLAGSHIP_PRODUCT');
  if (!flagshipOffer?.data?.item) return;

  const item = flagshipOffer.data.item;
  const translations = parseDMOTranslations(item.translations);
  if (!translations) return;

  const titleEl = document.querySelector('[data-product-target="title"]');
  if (titleEl) {
    const translated = getDMOTranslatedValue(translations, 'product_name', item.title);
    titleEl.textContent = translated.split('/')[0].trim();
  }

  const descEl = document.querySelector('[data-product-target="description"]');
  if (descEl) {
    let desc = getDMOTranslatedValue(translations, 'product_description', '');
    if (!desc?.trim()) {
      const short = getDMOTranslatedValue(translations, 'short_description', '');
      if (short?.trim()) {
        try {
          const parsed = JSON.parse(short);
          desc = parsed?.children?.[0]?.children?.[0]?.value || short;
        } catch {
          desc = short;
        }
      }
    }
    descEl.innerHTML = desc?.trim() ? desc : (item.productDescription || item.overview || '');
  }

  applyButtonTranslations();
}

// ─── Button translations ──────────────────────────────────────────────────────

/** Hardcoded locale → button text map. Exposed globally so other scripts can read it. */
const buttonTranslations = {
  'en-US': {
    subscribe: 'Subscribe & Save',
    subscribeNow: 'Get it now',
    monthlySubscription: 'Monthly Subscription',
    addToCart: 'Add to Cart',
    inCart: 'In Cart',
    noCommitments: 'No Commitments - Pause or Cancel Anytime',
    getTheApp: 'Get the app',
    free: 'FREE',
    freeTrialShort: 'Free Trial'
  },
  'es-US': {
    subscribe: 'Suscríbete y Ahorra',
    subscribeNow: 'Obténlo ahora',
    monthlySubscription: 'Suscripción Mensual',
    addToCart: 'Agregar al Carrito',
    inCart: 'En el Carrito',
    noCommitments: 'Sin Compromisos - Pausa o Cancela en Cualquier Momento',
    getTheApp: 'Obtener la aplicación',
    free: 'GRATIS',
    freeTrialShort: 'Prueba gratuita'
  },
  'es-ES': {
    subscribe: 'Suscríbete y Ahorra',
    subscribeNow: 'Obténlo ahora',
    monthlySubscription: 'Suscripción Mensual',
    addToCart: 'Agregar al Carrito',
    inCart: 'En el Carrito',
    noCommitments: 'Sin Compromisos - Pausa o Cancela en Cualquier Momento',
    getTheApp: 'Obtener la aplicación',
    free: 'GRATIS',
    freeTrialShort: 'Prueba gratuita'
  },
  'fr-CA': {
    subscribe: "S'abonner et Économiser",
    subscribeNow: 'Obtenez-le maintenant',
    monthlySubscription: 'Abonnement Mensuel',
    addToCart: 'Ajouter au Panier',
    inCart: 'Dans le Panier',
    noCommitments: 'Sans Engagement - Mettre en Pause ou Annuler à Tout Moment',
    getTheApp: "Obtenir l'application",
    free: 'GRATUIT',
    freeTrialShort: 'Essai gratuit'
  },
  'fr-US': {
    subscribe: "S'abonner et Économiser",
    subscribeNow: 'Obtenez-le maintenant',
    monthlySubscription: 'Abonnement Mensuel',
    addToCart: 'Ajouter au Panier',
    inCart: 'Dans le Panier',
    noCommitments: 'Sans Engagement - Mettre en Pause ou Annuler à Tout Moment',
    getTheApp: "Obtenir l'application",
    free: 'GRATUIT',
    freeTrialShort: 'Essai gratuit'
  },
  'fr-FR': {
    subscribe: "S'abonner et Économiser",
    subscribeNow: 'Obtenez-le maintenant',
    monthlySubscription: 'Abonnement Mensuel',
    addToCart: 'Ajouter au Panier',
    inCart: 'Dans le Panier',
    noCommitments: 'Sans Engagement - Mettre en Pause ou Annuler à Tout Moment',
    getTheApp: "Obtenir l'application",
    free: 'GRATUIT',
    freeTrialShort: 'Essai gratuit'
  },
  'en-CA': {
    subscribe: 'Subscribe & Save',
    subscribeNow: 'Get it now',
    monthlySubscription: 'Monthly Subscription',
    addToCart: 'Add to Cart',
    inCart: 'In Cart',
    noCommitments: 'No Commitments - Pause or Cancel Anytime',
    getTheApp: 'Get the app',
    free: 'FREE',
    freeTrialShort: 'Free Trial'
  },
  'nl-NL': {
    subscribe: 'Abonneer & Bespaar',
    subscribeNow: 'Nu verkrijgen',
    monthlySubscription: 'Maandelijks Abonnement',
    addToCart: 'In Winkelwagen',
    inCart: 'In Winkelwagen',
    noCommitments: 'Geen Verplichtingen - Pauzeer of Annuleer op Elk Moment',
    getTheApp: 'Download de app',
    free: 'GRATIS',
    freeTrialShort: 'Gratis proefperiode'
  },
  'de-DE': {
    subscribe: 'Abonnieren & Sparen',
    subscribeNow: 'Jetzt sichern',
    monthlySubscription: 'Monatliches Abonnement',
    addToCart: 'In den Warenkorb',
    inCart: 'Im Warenkorb',
    noCommitments: 'Keine Verpflichtungen - Jederzeit pausieren oder kündigen',
    getTheApp: 'App herunterladen',
    free: 'KOSTENLOS',
    freeTrialShort: 'Kostenlose Testversion'
  },
  'it-IT': {
    subscribe: 'Abbonati e Risparmia',
    subscribeNow: 'Ottienilo ora',
    monthlySubscription: 'Abbonamento Mensile',
    addToCart: 'Aggiungi al Carrello',
    inCart: 'Nel Carrello',
    noCommitments: 'Nessun Impegno - Metti in Pausa o Annulla in Qualsiasi Momento',
    getTheApp: "Scarica l'app",
    free: 'GRATUITO',
    freeTrialShort: 'Prova gratuita'
  }
};

/**
 * Applies locale-appropriate text to subscribe/one-time purchase button elements.
 * Updates only the text node (not the price child element) to prevent DOM flash.
 */
function applyButtonTranslations() {
  const section = document.querySelector('.enhanced-product-section');
  if (section?.getAttribute('data-app-only-mode') === 'true') return;

  const locale = getCurrentLocale();
  const langCode = locale.split('-')[0];
  const trans = buttonTranslations[locale]
    || buttonTranslations[Object.keys(buttonTranslations).find(k => k.startsWith(langCode + '-')) || '']
    || buttonTranslations['en-US'];

  document.querySelectorAll('.subscribe-text').forEach(el => {
    const priceEl = el.querySelector('.cart-price');
    if (priceEl) {
      const node = Array.from(el.childNodes).find(n => n.nodeType === Node.TEXT_NODE);
      if (node) { node.nodeValue = `${trans.subscribe} - `; }
      else { el.insertBefore(document.createTextNode(`${trans.subscribe} - `), priceEl); }
    } else {
      el.textContent = trans.subscribe;
    }
  });

  document.querySelectorAll('.onetime-text').forEach(el => {
    const priceEl = el.querySelector('.cart-price');
    if (priceEl) {
      const node = Array.from(el.childNodes).find(n => n.nodeType === Node.TEXT_NODE);
      if (node) { node.nodeValue = `${trans.addToCart} - `; }
      else { el.insertBefore(document.createTextNode(`${trans.addToCart} - `), priceEl); }
    } else {
      el.textContent = trans.addToCart;
    }
  });
}

// ─── Product page translations ────────────────────────────────────────────────

/**
 * Gets a translated field from window.productTranslations for the current locale,
 * trying multiple locale format variations before returning the fallback.
 */
function getTranslatedProductValue(field, fallback) {
  if (!window.productTranslations || typeof window.productTranslations !== 'object') return fallback;
  const locale = getCurrentLocale();
  const variations = [
    locale, locale.toUpperCase(), locale.toLowerCase(),
    `${locale.split('-')[0].toLowerCase()}-${locale.split('-')[1].toUpperCase()}`
  ];
  for (const l of variations) {
    if (window.productTranslations[l]) {
      const v = window.productTranslations[l].variant?.[field];
      if (v && typeof v === 'string' && v.trim()) return v;
      const p = window.productTranslations[l].product?.[field];
      if (p && typeof p === 'string' && p.trim()) return p;
    }
  }
  return fallback;
}

/**
 * Applies variant translations to the product title and description.
 * Falls back to window.originalProductText when no translation is found.
 */
function applyProductTranslations() {
  const hasTranslations = window.productTranslations
    && typeof window.productTranslations === 'object'
    && Object.keys(window.productTranslations).length > 0;

  const titleEl = document.querySelector('[data-product-target="title"]');
  if (titleEl) {
    const variantKey = String(window.selectedVariantId || '');
    const metafieldName = window.variantProductName?.[variantKey] || '';
    const titleFallback = metafieldName || window.originalProductText?.title || '';
    const src = hasTranslations
      ? getTranslatedProductValue('product_name', titleFallback)
      : titleFallback;
    titleEl.textContent = src.split('/')[0].trim();
  }

  const descEl = document.querySelector('[data-product-target="description"]');
  if (descEl) {
    const variantKey = String(window.selectedVariantId || '');
    const metafieldDesc = window.variantProductDescription?.[variantKey] || '';
    if (hasTranslations) {
      let desc = getTranslatedProductValue('product_description', '');
      if (!desc?.trim()) {
        const short = getTranslatedProductValue('short_description', '');
        if (short?.trim()) {
          try {
            const parsed = JSON.parse(short);
            desc = parsed?.children?.[0]?.children?.[0]?.value
              || (parsed?.type === 'root' ? parsed?.children?.[0]?.children?.[0]?.value : null)
              || short;
          } catch {
            desc = short;
          }
        }
      }
      descEl.innerHTML = desc?.trim()
        ? desc
        : (metafieldDesc || window.originalProductText?.description || '');
    } else {
      descEl.innerHTML = metafieldDesc || window.originalProductText?.description || '';
    }
  }

  applyButtonTranslations();
}

// ─── Tab content ──────────────────────────────────────────────────────────────

/** Decodes HTML entities in a string (e.g. &quot; → "). */
function decodeHtmlEntities(text) {
  const el = document.createElement('textarea');
  el.innerHTML = text;
  return el.value;
}

/**
 * Parses a Shopify rich text metafield JSON value and extracts clean HTML.
 * Returns the original value unchanged if it is not rich text JSON.
 */
function parseRichTextMetafield(value) {
  if (!value || typeof value !== 'string') return value;
  if (value.includes('&quot;') || value.includes('&lt;') || value.includes('&gt;')) {
    value = decodeHtmlEntities(value);
  }
  try {
    let parsed;
    try {
      parsed = JSON.parse(value);
    } catch {
      parsed = JSON.parse(
        value.replace(/\r\n/g, '\\n').replace(/\n/g, '\\n').replace(/\r/g, '\\n').replace(/\t/g, '\\t')
      );
    }
    if (parsed?.type === 'root' && Array.isArray(parsed.children)) {
      let html = '';
      for (const child of parsed.children) {
        if (child.type === 'paragraph') {
          for (const node of child.children || []) {
            if (node.type === 'text' && node.value) html += node.value;
          }
        }
      }
      html = html.replace(/<p>\s*<p>/gi, '<p>').replace(/<\/p>\s*<\/p>/gi, '</p>').replace(/\s+/g, ' ').trim();
      return html;
    }
    return value;
  } catch {
    // JSON parsing failed — strip known rich text wrapper strings as a fallback
    if (value.includes('{"type":"root"')) {
      const match = value.match(/"value"\s*:\s*"([\s\S]*)"\s*\}\s*\]\s*\}\s*\]\s*\}/);
      if (match) return match[1].trim();
    }
    return value;
  }
}

/** Converts a metafield value (string or object) to a clean HTML string. */
function metafieldToString(value) {
  if (!value) return '';
  if (typeof value === 'string') return parseRichTextMetafield(value) || value;
  if (typeof value === 'object') {
    if (value.value) return metafieldToString(value.value);
    try { return JSON.stringify(value); } catch { return ''; }
  }
  return String(value);
}

/**
 * Gets a translated tab field value, respecting locale.
 * For English locales returns the raw metafield string; for others checks window.productTranslations.
 */
function getTranslatedTabValue(field, fallback) {
  const locale = getCurrentLocale();
  if (locale.startsWith('en-')) return metafieldToString(fallback);
  if (!window.productTranslations || typeof window.productTranslations !== 'object') return metafieldToString(fallback);
  const variations = [
    locale, locale.toUpperCase(), locale.toLowerCase(),
    `${locale.split('-')[0].toLowerCase()}-${locale.split('-')[1].toUpperCase()}`
  ];
  for (const l of variations) {
    if (window.productTranslations[l]) {
      const v = window.productTranslations[l].variant?.[field];
      if (v && typeof v === 'string' && v.trim()) return v;
      const p = window.productTranslations[l].product?.[field];
      if (p && typeof p === 'string' && p.trim()) return p;
    }
  }
  return metafieldToString(fallback);
}

/**
 * Updates product tab elements (Overview, Benefits, Ingredients, Instructions)
 * with content from window.currentVariantMetafields and translations.
 */
function applyProductTabTranslations() {
  if (!window.currentVariantMetafields) return;

  const tabs = [
    { selector: '[data-block-id="overview-tab"]',     field: 'overview' },
    { selector: '[data-block-id="benefits-tab"]',     field: 'features_and_benefits' },
    { selector: '[data-block-id="ingredients-tab"]',  field: 'ingredients' },
    { selector: '[data-block-id="instructions-tab"]', field: 'instructions' }
  ];

  tabs.forEach(({ selector, field }) => {
    const el = document.querySelector(selector);
    if (!el) return;
    let content = getTranslatedTabValue(field, window.currentVariantMetafields[field] || '');
    if (content) {
      content = content.replace(/#clean#/g, '').replace(/>\s*#clean#\s*</g, '><').trim();
      if (content === '-') content = '';
    }
    if (content?.trim()) el.innerHTML = content;

    if (field === 'ingredients') {
      el.querySelector('[data-allergen-info]')?.remove();
      let allergenInfo = getTranslatedTabValue('ingredients_allergen_info', window.currentVariantMetafields['ingredients_allergen_info'] || '');
      if (allergenInfo) {
        allergenInfo = allergenInfo.replace(/#clean#/g, '').replace(/>\s*#clean#\s*</g, '><').trim();
        if (allergenInfo === '-') allergenInfo = '';
      }
      if (allergenInfo?.trim()) {
        const div = document.createElement('div');
        div.setAttribute('data-allergen-info', 'true');
        div.innerHTML = allergenInfo;
        el.appendChild(div);
      }
    }
  });
}

// ─── Global exports ───────────────────────────────────────────────────────────

window.getCurrentLocale          = getCurrentLocale;
window.parseDMOTranslations      = parseDMOTranslations;
window.getDMOTranslatedValue     = getDMOTranslatedValue;
window.applyDMOTranslations      = applyDMOTranslations;
window.buttonTranslations        = buttonTranslations;
window.applyButtonTranslations   = applyButtonTranslations;
window.getTranslatedProductValue = getTranslatedProductValue;
window.applyProductTranslations  = applyProductTranslations;
window.decodeHtmlEntities        = decodeHtmlEntities;
window.parseRichTextMetafield    = parseRichTextMetafield;
window.metafieldToString         = metafieldToString;
window.getTranslatedTabValue     = getTranslatedTabValue;
window.applyProductTabTranslations = applyProductTabTranslations;
